import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import ScrollToTop from './components/ScrollToTop';
import PWAInstallPrompt from './components/PWAInstallPrompt';
// Landing page - static HTML loaded at "/" for SEO
import Landing from './pages/LandingRedirect';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ResendVerification from './pages/ResendVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import AddBet from './pages/AddBet';
import BetHistory from './pages/BetHistory';
import Reports from './pages/Reports';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import TipPurchaseSuccess from './pages/TipPurchaseSuccess';
import ServiceSubscriptionSuccess from './pages/ServiceSubscriptionSuccess';
import TipsterWallet from './pages/tipster/TipsterWallet';
import AdminDashboard from './pages/AdminDashboard';
import AdminCRM from './pages/AdminCRM';
import AdminTables from './pages/AdminTables';
import AdminAds from './pages/AdminAds';
import AdminTips from './pages/AdminTips';
import AdminWithdrawals from './pages/AdminWithdrawals';
import AdminPayments from './pages/AdminPayments';
import AdminPredictionHistory from './pages/AdminPredictionHistory';
import SMTPSettings from './pages/SMTPSettings';
import PaymentSettings from './pages/PaymentSettings';
import GeneralSettings from './pages/GeneralSettings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Advertising from './pages/Advertising';
import GDPR from './pages/GDPR';
import AffiliateBanners from './pages/AffiliateBanners';
import BecomeTipster from './pages/BecomeTipster';
import TipServices from './pages/tipster/TipServices';
import ServiceDetail from './pages/tipster/ServiceDetail';
import AddService from './pages/tipster/AddService';
import BettingStrategies from './pages/BettingStrategies';
import StrategyDetail from './pages/StrategyDetail';
import AdminStrategies from './pages/AdminStrategies';
import PredictionsAI from './pages/PredictionsAI';
import StatsQuery from './pages/StatsQuery';
import DailyTips from './pages/DailyTips';
import DebugAuth from './pages/DebugAuth';
import AuthCallback from './pages/AuthCallback';
import UserGuide from './pages/UserGuide';
import Blog from './pages/blog/Blog';
import BlogArticle from './pages/blog/BlogArticle';
import Maintenance from './pages/Maintenance';
import BlogManagement from './pages/admin/BlogManagement';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AdminApiDebug from './pages/AdminApiDebug';
import { Toaster } from './components/ui/sonner';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { DateFormatProvider } from './contexts/DateFormatContext';
import { OddsFormatProvider } from './contexts/OddsFormatContext';
import GoogleAnalytics from './components/GoogleAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();
export const AppSettingsContext = React.createContext();

// Global axios interceptor to handle session expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error is 401 (Unauthorized) or 403 (Forbidden)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Check if we're not already on a public page
      const publicPaths = ['/', '/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/resend-verification', '/blog', '/betting-strategies', '/predictions-ai', '/privacy-policy', '/terms-of-use', '/gdpr', '/advertising', '/become-tipster', '/user-guide', '/subscription', '/daily-tips'];
      const currentPath = window.location.pathname;
      const isPublicPage = publicPaths.some(path => currentPath === path || currentPath.startsWith('/blog/') || currentPath.startsWith('/betting-strategies/'));
      
      if (!isPublicPage) {
        // Clear session data
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        
        // Redirect to login
        window.location.href = '/login?session_expired=true';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appSettings, setAppSettings] = useState(() => {
    // Load from cache immediately for instant display
    const cached = localStorage.getItem('app_settings');
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        return { app_name: '', app_logo_url: '', app_icon_url: '', maintenance_mode: false, maintenance_message: '' };
      }
    }
    return { app_name: '', app_logo_url: '', app_icon_url: '', maintenance_mode: false, maintenance_message: '' };
  });

  // Load app settings immediately
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/general-settings/public`);
        if (response.data) {
          const logoUrl = response.data.app_logo_url ? 
            (response.data.app_logo_url.startsWith('http') ? response.data.app_logo_url : `${BACKEND_URL}${response.data.app_logo_url}`) : '';
          const iconUrl = response.data.app_icon_url ? 
            (response.data.app_icon_url.startsWith('http') ? response.data.app_icon_url : `${BACKEND_URL}${response.data.app_icon_url}`) : '';
          
          const settings = {
            app_name: response.data.app_name || 'BettingTracker.Pro',
            app_logo_url: logoUrl,
            app_icon_url: iconUrl,
            contact_email: response.data.contact_email || '',
            maintenance_mode: response.data.maintenance_mode === true, // Ensure boolean
            maintenance_message: response.data.maintenance_message || 'We are currently performing scheduled maintenance. Please check back soon.'
          };
          
          console.log('[App] Settings loaded:', { maintenance_mode: settings.maintenance_mode });
          
          setAppSettings(settings);
          // Cache in localStorage for instant next load
          localStorage.setItem('app_settings', JSON.stringify(settings));
          
          // Update page title and favicon
          document.title = settings.app_name;
          if (iconUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = iconUrl;
          }
        }
      } catch (error) {
        console.error('Error loading app settings:', error);
      }
    };
    
    // Clear old cache that might not have maintenance fields
    const cached = localStorage.getItem('app_settings');
    if (cached) {
      try {
        const parsedCache = JSON.parse(cached);
        if (typeof parsedCache.maintenance_mode === 'undefined') {
          console.log('[App] Clearing old settings cache');
          localStorage.removeItem('app_settings');
        }
      } catch (e) {
        localStorage.removeItem('app_settings');
      }
    }
    
    loadSettings();
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('=== AUTH CHECK START ===');
      const token = localStorage.getItem('session_token');
      
      if (token) {
        console.log('Token found in localStorage, verifying...');
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // Store user in localStorage for analytics tracking (admin exclusion)
          localStorage.setItem('user', JSON.stringify(response.data));
          console.log('✅ User restored from token:', response.data.email);
        } catch (error) {
          console.error('❌ Token invalid, removing...');
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
        }
      } else {
        console.log('No token in localStorage');
      }
      
      setLoading(false);
      console.log('=== AUTH CHECK END ===');
    };

    checkAuth();
  }, []);

  const logout = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, null, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Maintenance mode check - Allow admins to bypass
  const isAdmin = user?.is_admin === true;
  const isMaintenanceMode = appSettings?.maintenance_mode === true;
  
  if (isMaintenanceMode && !isAdmin) {
    // Show maintenance page for non-admin users
    // Allow auth-related pages so admins can log in
    const currentPath = window.location.pathname;
    const allowedPaths = ['/login', '/signup', '/auth/callback', '/verify-email', '/forgot-password', '/reset-password'];
    const isAllowedPath = allowedPaths.some(path => currentPath.startsWith(path));
    
    if (!isAllowedPath) {
      return (
        <Maintenance 
          message={appSettings?.maintenance_message}
          appName={appSettings?.app_name}
          contactEmail={appSettings?.contact_email}
        />
      );
    }
  }

  return (
    <DateFormatProvider user={user}>
      <OddsFormatProvider user={user}>
        <CurrencyProvider user={user}>
          <AppSettingsContext.Provider value={appSettings}>
            <AuthContext.Provider value={{ user, setUser, logout }}>
            <BrowserRouter>
              <GoogleAnalytics />
              <ScrollToTop />
              <PWAInstallPrompt />
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
          <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/resend-verification" element={<ResendVerification />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/debug-auth" element={<DebugAuth />} />
          <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
          <Route path="/bets/add" element={user ? <AddBet /> : <Navigate to="/login" />} />
          <Route path="/bets" element={user ? <BetHistory /> : <Navigate to="/login" />} />
          <Route path="/reports" element={user ? <Reports /> : <Navigate to="/login" />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/subscription/success" element={user ? <SubscriptionSuccess /> : <Navigate to="/login" />} />
          <Route path="/tip-purchase/success" element={user ? <TipPurchaseSuccess /> : <Navigate to="/login" />} />
          <Route path="/service-subscription/success" element={user ? <ServiceSubscriptionSuccess /> : <Navigate to="/login" />} />
          <Route path="/tipster/wallet" element={user ? <TipsterWallet /> : <Navigate to="/login" />} />
          <Route path="/tip-services" element={user ? <TipServices /> : <Navigate to="/login" />} />
          <Route path="/tip-services/:serviceId" element={user ? <ServiceDetail /> : <Navigate to="/login" />} />
          <Route path="/add-service" element={user ? <AddService /> : <Navigate to="/login" />} />
          <Route path="/betting-strategies" element={user ? <BettingStrategies /> : <Navigate to="/login" />} />
          <Route path="/betting-strategies/:strategyId" element={user ? <StrategyDetail /> : <Navigate to="/login" />} />
          <Route path="/predictions-ai" element={user ? <PredictionsAI /> : <Navigate to="/login" />} />
          <Route path="/stats" element={user ? <StatsQuery /> : <Navigate to="/login" />} />
          <Route path="/daily-tips" element={<DailyTips />} />
          <Route path="/user-guide" element={<UserGuide />} />
          <Route path="/admin" element={user && user.is_admin ? <AdminDashboard /> : <Navigate to="/login" />} />
          <Route path="/admin/dashboard" element={user && user.is_admin ? <AdminDashboard /> : <Navigate to="/login" />} />
          <Route path="/admin/crm" element={user && user.is_admin ? <AdminCRM /> : <Navigate to="/login" />} />
          <Route path="/admin/tables" element={user && user.is_admin ? <AdminTables /> : <Navigate to="/login" />} />
          <Route path="/admin/ads" element={user && user.is_admin ? <AdminAds /> : <Navigate to="/login" />} />
          <Route path="/admin/tips" element={user && user.is_admin ? <AdminTips /> : <Navigate to="/login" />} />
          <Route path="/admin/withdrawals" element={user && user.is_admin ? <AdminWithdrawals /> : <Navigate to="/login" />} />
          <Route path="/admin/payments" element={user && user.is_admin ? <AdminPayments /> : <Navigate to="/login" />} />
          <Route path="/admin/strategies" element={user && user.is_admin ? <AdminStrategies /> : <Navigate to="/login" />} />
          <Route path="/admin/smtp-settings" element={user && user.is_admin ? <SMTPSettings /> : <Navigate to="/login" />} />
          <Route path="/admin/affiliate-banners" element={user && user.is_admin ? <AffiliateBanners /> : <Navigate to="/login" />} />
          <Route path="/admin/prediction-history" element={user && user.is_admin ? <AdminPredictionHistory /> : <Navigate to="/login" />} />
          <Route path="/admin/payment-settings" element={user && user.is_admin ? <PaymentSettings /> : <Navigate to="/login" />} />
          <Route path="/admin/general-settings" element={user && user.is_admin ? <GeneralSettings /> : <Navigate to="/login" />} />
          <Route path="/admin/blog" element={user && user.is_admin ? <BlogManagement /> : <Navigate to="/login" />} />
          <Route path="/admin/analytics" element={user && user.is_admin ? <AnalyticsDashboard /> : <Navigate to="/login" />} />
          <Route path="/admin/api-debug" element={user && user.is_admin ? <AdminApiDebug /> : <Navigate to="/login" />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/advertising" element={<Advertising />} />
          <Route path="/gdpr" element={<GDPR />} />
          <Route path="/become-tipster" element={<BecomeTipster />} />
        </Routes>
        <Toaster />
          </BrowserRouter>
          </AuthContext.Provider>
        </AppSettingsContext.Provider>
      </CurrencyProvider>
      </OddsFormatProvider>
    </DateFormatProvider>
  );
}

export default App;
